import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { TransitionLink } from '@/components/generals/page-transition'
import { navigation, siteConfig } from '@/constants/site'
import { footerWordmarkImage } from '@/constants/content'

/**
 * The line and the nav links, a thin divider, the copyright and social
 * links, then a giant "Auren" cropped at the bottom edge with a portrait
 * showing through the letters.
 */
export const Footer: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end end'] })
    const wordmarkY = useTransform(scrollYProgress, [0, 1], ['46%', '24%'])
    const photoPosition = useTransform(scrollYProgress, [0, 1], ['50% 15%', '50% 55%'])

    return (
        <footer ref={ref} id='site-footer' className='relative overflow-hidden border-t border-white/10 pt-24 md:pt-32'>
            <div className='relative z-10 px-4 md:px-[30px]'>
                <div className='flex flex-col gap-12 md:flex-row md:items-end md:justify-between'>
                    <p className='type-title max-w-[18ch] text-foreground'>
                        {siteConfig.footerLine}
                    </p>

                    <nav aria-label='Footer'>
                        <ul className='flex flex-wrap gap-x-8 gap-y-3 text-[15px]'>
                            {navigation.map(item => (
                                <li key={item.href}>
                                    <TransitionLink href={item.href} className='text-foreground/85 transition-colors hover:text-gold'>
                                        {item.label}
                                    </TransitionLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                <div className='mt-16 flex flex-col gap-4 border-t border-white/10 py-6 text-sm text-muted-foreground md:mt-24 md:flex-row md:items-center md:justify-between'>
                    <p>{siteConfig.copyright}</p>

                    <ul className='flex flex-wrap gap-6'>
                        {siteConfig.socials.map(social => (
                            <li key={social.label}>
                                <a
                                    href={social.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='transition-colors hover:text-gold'
                                >
                                    {social.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div aria-hidden='true' className='pointer-events-none relative -mt-[4vw] select-none'>
                <motion.p
                    style={{ y: wordmarkY }}
                    className='text-center text-[29vw] font-light leading-[0.78] tracking-[-0.075em]'
                >
                    <motion.span
                        className='inline-block bg-cover bg-clip-text text-transparent'
                        style={{
                            backgroundImage: `linear-gradient(to bottom, rgba(11, 11, 11, 0) 40%, rgba(11, 11, 11, 0.55)), url(${footerWordmarkImage})`,
                            backgroundPosition: photoPosition,
                            WebkitBackgroundClip: 'text'
                        }}
                    >
                        Auren
                    </motion.span>
                </motion.p>
            </div>
        </footer>
    )
}
